import { useMemo, useState } from 'react';
import Map from 'react-map-gl/maplibre';
import DeckGL from '@deck.gl/react';
import { ScatterplotLayer, ArcLayer } from '@deck.gl/layers';
import 'maplibre-gl/dist/maplibre-gl.css';

interface GenomeNeighborGeo {
  userId: string;
  displayName: string | null;
  similarity: number;
  concordance: number | null;
  latitude: number | null;
  longitude: number | null;
  city: string | null;
  region: string | null;
}

interface GenomeMapProps {
  selectedArtist: {
    userId: string;
    latitude: number | null;
    longitude: number | null;
  } | null;
  neighbors: GenomeNeighborGeo[];
  onArtistClick: (userId: string) => void;
}

const BASEMAP_STYLE = 'https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json';
const INITIAL_VIEW_STATE = {
  latitude: 39.8283,
  longitude: -98.5795,
  zoom: 3.5,
  pitch: 0,
  bearing: 0,
};

function simToColor(similarity: number): [number, number, number, number] {
  const alpha = Math.round(80 + similarity * 175);
  return [100, 210, 255, alpha];
}

function GenomeMap({ selectedArtist, neighbors, onArtistClick }: GenomeMapProps) {
  const [viewState, setViewState] = useState(INITIAL_VIEW_STATE);
  const [hovered, setHovered] = useState<GenomeNeighborGeo | null>(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  const geoNeighbors = useMemo(() => neighbors.filter((n) => n.latitude != null && n.longitude != null), [neighbors]);

  const hasSourceGeo = selectedArtist?.latitude != null && selectedArtist?.longitude != null;

  const layers = useMemo(() => {
    if (geoNeighbors.length === 0 && !hasSourceGeo) return [];

    const scatterData = [
      ...(hasSourceGeo
        ? [{ ...selectedArtist!, isSelected: true, similarity: 1, displayName: 'Selected' as string | null }]
        : []),
      ...geoNeighbors.map((n) => ({ ...n, isSelected: false })),
    ];

    const result: any[] = [];

    if (hasSourceGeo) {
      result.push(
        new ArcLayer({
          id: 'arcs',
          data: geoNeighbors,
          getSourcePosition: () => [selectedArtist!.longitude!, selectedArtist!.latitude!],
          getTargetPosition: (d: any) => [d.longitude, d.latitude],
          getSourceColor: [126, 231, 135, 200],
          getTargetColor: (d: any) => simToColor(d.similarity),
          getWidth: 2,
          greatCircle: true,
          pickable: false,
        })
      );
    }

    result.push(
      new ScatterplotLayer({
        id: 'points',
        data: scatterData,
        getPosition: (d: any) => [d.longitude, d.latitude],
        getFillColor: (d: any) => (d.isSelected ? [126, 231, 135, 220] : simToColor(d.similarity)),
        getRadius: (d: any) => (d.isSelected ? 7 : 5),
        radiusUnits: 'pixels' as const,
        pickable: true,
        onClick: (info: any) => {
          if (info.object?.userId) onArtistClick(info.object.userId);
        },
        onHover: (info: any) => {
          setHovered(info.object ?? null);
          setTooltipPos({ x: info.x ?? 0, y: info.y ?? 0 });
        },
      })
    );

    return result;
  }, [selectedArtist, geoNeighbors, hasSourceGeo, onArtistClick]);

  if (!selectedArtist) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-zinc-400">
        Select an artist to see their neighbors on the map
      </div>
    );
  }

  if (geoNeighbors.length === 0 && !hasSourceGeo) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-zinc-400">
        No geographic data available for this artist or their neighbors
      </div>
    );
  }

  return (
    <div className="relative h-full w-full">
      <DeckGL
        viewState={viewState}
        onViewStateChange={({ viewState: vs }) => setViewState(vs as typeof viewState)}
        controller
        layers={layers}
      >
        <Map mapStyle={BASEMAP_STYLE} />
      </DeckGL>

      {hovered && (
        <div
          className="pointer-events-none absolute z-10 max-w-xs rounded-lg bg-zinc-900 px-3 py-2 text-xs text-white shadow-lg"
          style={{ left: tooltipPos.x + 12, top: tooltipPos.y - 12 }}
        >
          <p className="font-medium">{hovered.displayName ?? 'Unknown'}</p>
          <p className="text-zinc-400">{[hovered.city, hovered.region].filter(Boolean).join(', ') || 'No location'}</p>
          <p className="text-zinc-400">Similarity: {hovered.similarity?.toFixed(4)}</p>
        </div>
      )}

      <div className="absolute bottom-4 left-4 flex items-center gap-3 rounded-lg bg-zinc-900/80 px-3 py-2 text-[11px] text-zinc-300 backdrop-blur">
        <span className="flex items-center gap-1">
          <span className="inline-block h-2.5 w-2.5 rounded-full bg-[rgb(126,231,135)]" /> Selected
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block h-2.5 w-2.5 rounded-full bg-[rgb(100,210,255)]" /> Neighbor
        </span>
        <span className="text-zinc-500">― Arc</span>
      </div>
    </div>
  );
}

export default GenomeMap;
